import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import {
  Download, FileText, FileSpreadsheet, Users, Car, CreditCard,
  ClipboardList, Calendar, BarChart3, Loader2, ChevronDown
} from 'lucide-react';
import { getAuthToken } from '../../services/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const REPORT_TYPES = [
  { id: 'summary', label: 'Community Summary', desc: 'Overview of all metrics in one report', icon: BarChart3, color: 'bg-indigo-600' },
  { id: 'traffic', label: 'Traffic Analytics', desc: 'Daily gate traffic with peak hour breakdown (IST)', icon: BarChart3, color: 'bg-emerald-700' },
  { id: 'users', label: 'Users Master Data', desc: 'All users with roles, villas, contact info', icon: Users, color: 'bg-slate-700' },
  { id: 'visitors', label: 'Visitor Logs', desc: 'All visitor entries with timestamps & status', icon: Users, color: 'bg-emerald-600' },
  { id: 'vehicles', label: 'Registered Vehicles', desc: 'Complete vehicle registry with details', icon: Car, color: 'bg-sky-600' },
  { id: 'invoices', label: 'Invoices & Payments', desc: 'Billing, collections & outstanding amounts', icon: CreditCard, color: 'bg-amber-600' },
  { id: 'staff-attendance', label: 'Staff Attendance', desc: 'Check-in/out logs with duration tracking', icon: ClipboardList, color: 'bg-violet-600' },
  { id: 'facility-bookings', label: 'Facility Bookings', desc: 'All bookings across community facilities', icon: Calendar, color: 'bg-rose-600' },
];

const PRESETS = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'custom', label: 'Custom Range' },
];

export function ExportsTab() {
  const [selectedReport, setSelectedReport] = useState(null);
  const [format, setFormat] = useState('pdf');
  const [preset, setPreset] = useState('this_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (reportId, fmt) => {
    const key = `${reportId}-${fmt}`;
    setDownloading(key);
    try {
      const params = new URLSearchParams({ format: fmt });
      if (preset === 'custom') {
        if (!startDate || !endDate) { toast.error('Please select both start and end dates'); setDownloading(null); return; }
        params.append('start_date', startDate);
        params.append('end_date', endDate);
      } else {
        params.append('preset', preset);
      }

      const response = await fetch(`${API_URL}/reports/${reportId}?${params.toString()}`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      });

      if (!response.ok) {
        const err = await response.json();
        toast.error(err.detail || 'Export failed');
        setDownloading(null);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch = disposition.match(/filename=(.+)/);
      a.href = url;
      a.download = filenameMatch ? filenameMatch[1] : `report.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} report downloaded`);
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download report');
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Date Range Controls */}
      <Card className="shadow-lg border-0" data-testid="export-controls-card">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Download className="w-5 h-5 text-indigo-600" />
            Export Reports
          </CardTitle>
          <p className="text-sm text-slate-500 mt-1">
            Download analytics reports as PDF or CSV for AGMs, audits, and record-keeping
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            {/* Preset selector */}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Period</Label>
              <div className="relative">
                <select
                  data-testid="export-preset-select"
                  value={preset}
                  onChange={(e) => setPreset(e.target.value)}
                  className="h-9 pl-3 pr-8 rounded-lg border border-slate-200 bg-white text-sm font-medium appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500"
                >
                  {PRESETS.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
                <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2 top-2.5 pointer-events-none" />
              </div>
            </div>

            {/* Custom date range */}
            {preset === 'custom' && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500">From</Label>
                  <Input
                    data-testid="export-start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="h-9 w-40"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500">To</Label>
                  <Input
                    data-testid="export-end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="h-9 w-40"
                  />
                </div>
              </>
            )}

            {/* Format toggle */}
            <div className="space-y-1.5">
              <Label className="text-xs text-slate-500">Format</Label>
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                <button
                  data-testid="export-format-pdf"
                  onClick={() => setFormat('pdf')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${
                    format === 'pdf'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <FileText className="w-3.5 h-3.5" /> PDF
                </button>
                <button
                  data-testid="export-format-csv"
                  onClick={() => setFormat('csv')}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors border-l ${
                    format === 'csv'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <FileSpreadsheet className="w-3.5 h-3.5" /> CSV
                </button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="export-reports-grid">
        {REPORT_TYPES.map(report => {
          const Icon = report.icon;
          const isDownloading = downloading === `${report.id}-${format}`;
          const isSelected = selectedReport === report.id;

          return (
            <Card
              key={report.id}
              data-testid={`export-card-${report.id}`}
              className={`shadow-md border-0 overflow-hidden transition-all cursor-pointer hover:shadow-xl hover:-translate-y-0.5 ${
                isSelected ? 'ring-2 ring-indigo-500' : ''
              }`}
              onClick={() => setSelectedReport(isSelected ? null : report.id)}
            >
              <div className={`${report.color} px-4 py-3`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5 text-white" />
                    <h3 className="font-semibold text-white text-sm">{report.label}</h3>
                  </div>
                  <Badge className="bg-white/20 text-white text-[10px]">
                    {format.toUpperCase()}
                  </Badge>
                </div>
              </div>
              <CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-3">{report.desc}</p>
                <div className="flex gap-2">
                  <Button
                    data-testid={`download-${report.id}-pdf`}
                    size="sm"
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-xs h-8"
                    disabled={!!downloading}
                    onClick={(e) => { e.stopPropagation(); handleDownload(report.id, 'pdf'); }}
                  >
                    {downloading === `${report.id}-pdf` ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <><FileText className="w-3.5 h-3.5 mr-1" /> PDF</>
                    )}
                  </Button>
                  <Button
                    data-testid={`download-${report.id}-csv`}
                    size="sm"
                    variant="outline"
                    className="flex-1 text-xs h-8"
                    disabled={!!downloading}
                    onClick={(e) => { e.stopPropagation(); handleDownload(report.id, 'csv'); }}
                  >
                    {downloading === `${report.id}-csv` ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <><FileSpreadsheet className="w-3.5 h-3.5 mr-1" /> CSV</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Help text */}
      <div className="text-center text-xs text-slate-400 py-2">
        Reports include your community branding and are branded with <span className="font-medium text-slate-500">gatekeeper.homes</span>
      </div>
    </div>
  );
}
